import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

/**
 * The safety incident log — the triage side of monitoring.
 *
 * The monitor raises events on its own; this is where a person works through
 * them: filter to what matters, look at the frame it fired on, and acknowledge
 * the ones dealt with so the open list is the real to-do, not a history that
 * never shrinks.
 */

type Verdict = 'correct' | 'false_alarm' | 'missed';

interface SafetyEvent {
  id: string;
  time: string;
  kind: string;
  severity: string;
  detail: string;
  overlayKey: string | null;
  acknowledged: boolean;
  feedback: Verdict | null;
  blur: boolean;
}

const SEV_COLOUR: Record<string, string> = {
  critical: 'var(--bad)',
  warning: 'var(--warn)',
  info: 'var(--teal)',
};
const KIND_LABEL: Record<string, string> = {
  ppe_violation: 'PPE violation',
  fire: 'Fire / smoke',
  near_miss: 'Near miss',
  intrusion: 'Zone intrusion',
  occupancy: 'Occupancy',
};

type Filter = 'all' | 'unacked' | 'critical' | 'warning';

export function SafetyEventsView() {
  const [events, setEvents] = useState<SafetyEvent[]>([]);
  const [filter, setFilter] = useState<Filter>('unacked');
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(() => {
    const p = new URLSearchParams({ limit: '100' });
    if (filter === 'unacked') p.set('unacked', 'true');
    if (filter === 'critical') p.set('severity', 'critical');
    if (filter === 'warning') p.set('severity', 'warning');
    fetch(`/api/safety/events?${p}`)
      .then((r) => (r.ok ? r.json() : []))
      .then(setEvents)
      .catch(() => undefined);
  }, [filter]);
  useEffect(() => load(), [load]);

  const ack = async (id: string, acknowledged: boolean) => {
    await fetch(`/api/safety/events/${id}/ack`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ acknowledged }),
    });
    load();
  };

  // Toggle a verdict: clicking the active one clears it (verdict: null). These
  // corrections are the training signal — a false alarm becomes a hard-negative
  // image when the models are retrained.
  const sendFeedback = async (id: string, current: Verdict | null, verdict: Verdict) => {
    await fetch(`/api/safety/events/${id}/feedback`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ verdict: current === verdict ? null : verdict }),
    });
    load();
  };

  const FILTERS: Array<{ key: Filter; label: string }> = [
    { key: 'unacked', label: 'Open' },
    { key: 'critical', label: 'Critical' },
    { key: 'warning', label: 'Warning' },
    { key: 'all', label: 'All' },
  ];

  return (
    <div>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <h1 className="view-title">Safety events</h1>
        <Link to="/safety" style={{ fontSize: 13 }}>← Monitor</Link>
      </div>

      <div className="row" style={{ marginBottom: 16, gap: 6 }}>
        {FILTERS.map((f) => (
          <button key={f.key} className={`btn sm${filter === f.key ? ' primary' : ''}`} onClick={() => setFilter(f.key)}>
            {f.label}
          </button>
        ))}
      </div>

      {events.length === 0 ? (
        <div className="panel muted">Nothing here — {filter === 'unacked' ? 'no open events.' : 'no events match.'}</div>
      ) : (
        <div className="grid" style={{ gap: 8 }}>
          {events.map((e) => (
            <EventRow
              key={e.id}
              event={e}
              expanded={expanded === e.id}
              onToggle={() => setExpanded((x) => (x === e.id ? null : e.id))}
              onAck={() => void ack(e.id, !e.acknowledged)}
              onFeedback={(v) => void sendFeedback(e.id, e.feedback, v)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function EventRow({
  event,
  expanded,
  onToggle,
  onAck,
  onFeedback,
}: {
  event: SafetyEvent;
  expanded: boolean;
  onToggle: () => void;
  onAck: () => void;
  onFeedback: (v: Verdict) => void;
}) {
  // Served through the backend so faces can be blurred at playback (GDPR) when the
  // tenant/camera asks for it — never a direct storage URL for these frames.
  const imgUrl = event.overlayKey ? `/api/safety/events/${event.id}/image` : null;
  const colour = SEV_COLOUR[event.severity] ?? 'var(--border)';
  return (
    <div
      className="panel"
      style={{ padding: 12, borderLeft: `3px solid ${colour}`, opacity: event.acknowledged ? 0.6 : 1 }}
    >
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
        <div style={{ minWidth: 0 }}>
          <div className="row" style={{ gap: 10, alignItems: 'baseline' }}>
            <strong style={{ color: colour }}>{KIND_LABEL[event.kind] ?? event.kind}</strong>
            <span className="muted" style={{ fontSize: 12 }}>{new Date(event.time).toLocaleString('nl-NL')}</span>
            {event.acknowledged && <span className="muted" style={{ fontSize: 12 }}>· acknowledged</span>}
            {event.blur && <span title="Faces blurred for privacy" style={{ fontSize: 12, color: 'var(--teal)' }}>· faces blurred</span>}
          </div>
          <div className="muted" style={{ fontSize: 13, marginTop: 2 }}>{event.detail}</div>
        </div>
        <div className="row" style={{ gap: 6, flexShrink: 0 }}>
          {event.overlayKey && (
            <button className="btn sm" onClick={onToggle}>{expanded ? 'Hide' : 'View'}</button>
          )}
          <button className={`btn sm${event.acknowledged ? '' : ' primary'}`} onClick={onAck}>
            {event.acknowledged ? 'Reopen' : 'Acknowledge'}
          </button>
        </div>
      </div>
      <div className="row" style={{ gap: 6, alignItems: 'center', marginTop: 8 }}>
        <span className="muted" style={{ fontSize: 12 }}>Is this right?</span>
        <FeedbackButton label="✓ Correct" active={event.feedback === 'correct'} tone="var(--good)" onClick={() => onFeedback('correct')} />
        <FeedbackButton label="✕ False alarm" active={event.feedback === 'false_alarm'} tone="var(--bad)" onClick={() => onFeedback('false_alarm')} />
        <FeedbackButton label="⊘ Missed something" active={event.feedback === 'missed'} tone="var(--warn)" onClick={() => onFeedback('missed')} />
        {event.feedback && <span className="muted" style={{ fontSize: 11 }}>— saved for training</span>}
      </div>
      {expanded && imgUrl && <BoxFeedbackImage eventId={event.id} />}
    </div>
  );
}

function FeedbackButton({ label, active, tone, onClick }: { label: string; active: boolean; tone: string; onClick: () => void }) {
  return (
    <button
      className="btn sm"
      onClick={onClick}
      style={active ? { borderColor: tone, color: tone, fontWeight: 600 } : undefined}
      title="Your answer trains the models — false alarms become negative examples, missed cases become ones to learn."
    >
      {label}
    </button>
  );
}

interface Det {
  kind: string;
  x: number;
  y: number;
  w: number;
  h: number;
  confidence: number;
  meta?: { ppe?: { vest?: string; helmet?: string } };
}
interface BoxFb {
  boxIndex: number;
  verdict: 'correct' | 'false';
  actualLabel: string | null;
}

/**
 * The raw event frame with clickable boxes: judge each detection right or wrong,
 * and if wrong say what it actually is. This is the fine-grained training signal —
 * a false box is a hard-negative for its class, a relabel a positive for the right
 * one. The frame is the un-annotated one (raw=1); the coloured boxes are ours.
 */
function BoxFeedbackImage({ eventId }: { eventId: string }) {
  const [dets, setDets] = useState<Det[]>([]);
  const [fb, setFb] = useState<Record<number, BoxFb>>({});
  const [sel, setSel] = useState<number | null>(null);
  const [actual, setActual] = useState('');

  useEffect(() => {
    fetch(`/api/safety/events/${eventId}/detections`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d: { detections: Det[]; feedback: BoxFb[] } | null) => {
        if (!d) return;
        setDets(d.detections ?? []);
        const m: Record<number, BoxFb> = {};
        for (const f of d.feedback ?? []) m[f.boxIndex] = f;
        setFb(m);
      })
      .catch(() => undefined);
  }, [eventId]);

  const judge = async (idx: number, kind: string, verdict: 'correct' | 'false' | null, actualLabel?: string) => {
    await fetch(`/api/safety/events/${eventId}/boxes/${idx}/feedback`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ verdict, boxKind: kind, actualLabel: actualLabel ?? null }),
    });
    setFb((m) => {
      const n = { ...m };
      if (verdict === null) delete n[idx];
      else n[idx] = { boxIndex: idx, verdict, actualLabel: actualLabel ?? null };
      return n;
    });
  };

  const boxColour = (idx: number): string => {
    const v = fb[idx]?.verdict;
    return v === 'correct' ? 'var(--good)' : v === 'false' ? 'var(--bad)' : 'var(--warn)';
  };

  const judged = Object.keys(fb).length;
  return (
    <div style={{ marginTop: 10 }}>
      <div style={{ position: 'relative', display: 'inline-block', maxWidth: '100%', lineHeight: 0 }}>
        <img
          src={`/api/safety/events/${eventId}/image?raw=1`}
          alt="event frame"
          style={{ display: 'block', maxWidth: '100%', width: 640, borderRadius: 8, border: '1px solid var(--border)' }}
        />
        {dets.map((d, i) => (
          <button
            key={i}
            title={`${d.kind} ${Math.round(d.confidence * 100)}%`}
            onClick={() => {
              setSel(i);
              setActual(fb[i]?.actualLabel ?? '');
            }}
            style={{
              position: 'absolute',
              left: `${d.x * 100}%`,
              top: `${d.y * 100}%`,
              width: `${d.w * 100}%`,
              height: `${d.h * 100}%`,
              border: `2px solid ${boxColour(i)}`,
              background: sel === i ? 'rgba(255,255,255,0.10)' : 'transparent',
              padding: 0,
              cursor: 'pointer',
              borderRadius: 3,
            }}
          />
        ))}
      </div>
      <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
        {dets.length === 0 ? 'No boxes on this frame.' : `Click a box to judge it — ${judged}/${dets.length} done.`}
      </div>

      {sel != null && dets[sel] && (
        <div className="panel" style={{ marginTop: 8, padding: 10 }}>
          <div className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <strong style={{ fontSize: 13 }}>
              Box #{sel + 1}: {dets[sel]!.kind}
            </strong>
            <button
              className="btn sm"
              style={fb[sel]?.verdict === 'correct' ? { borderColor: 'var(--good)', color: 'var(--good)' } : undefined}
              onClick={() => void judge(sel, dets[sel]!.kind, fb[sel]?.verdict === 'correct' ? null : 'correct')}
            >
              ✓ Correct
            </button>
            <button
              className="btn sm"
              style={fb[sel]?.verdict === 'false' ? { borderColor: 'var(--bad)', color: 'var(--bad)' } : undefined}
              onClick={() => void judge(sel, dets[sel]!.kind, fb[sel]?.verdict === 'false' ? null : 'false', actual || undefined)}
            >
              ✕ False
            </button>
            {fb[sel]?.verdict === 'false' && (
              <input
                placeholder={`What is it actually? (e.g. ${dets[sel]!.kind === 'fire' ? 'lamp, reflection' : 'not a person'})`}
                value={actual}
                onChange={(e) => setActual(e.target.value)}
                onBlur={() => void judge(sel, dets[sel]!.kind, 'false', actual || undefined)}
                style={{ fontSize: 13, flex: 1, minWidth: 200 }}
              />
            )}
          </div>
          <div className="muted" style={{ fontSize: 11, marginTop: 6 }}>
            False = wrong detection (becomes a negative example). Naming what it really is helps the model learn the difference.
          </div>
        </div>
      )}
    </div>
  );
}
